import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Sparkles, Download, RefreshCw, User, MapPin, Briefcase, GraduationCap, Phone, Heart, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';

export default function PotentialMatchesTab() {
  const { token, API } = useAuth();
  const { lang } = useLang();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [availableRounds, setAvailableRounds] = useState([]);
  const [selectedRound, setSelectedRound] = useState(null);
  const [resetting, setResetting] = useState(false);
  const cardRefs = useRef({});

  const headers = { Authorization: `Bearer ${token}` };

  const fetchMatches = useCallback(async (roundParam) => {
    setLoading(true);
    try {
      const url = roundParam ? `${API}/admin/potential-matches?round=${roundParam}&limit=200` : `${API}/admin/potential-matches?limit=200`;
      const res = await axios.get(url, { headers });
      setMatches(res.data.matches || []);
      setAvailableRounds(res.data.available_rounds || []);
      setSelectedRound(res.data.round || null);
    } catch (err) { console.error('[PotentialMatchesTab] fetchMatches failed:', err); }
    setLoading(false);
  }, [API, token]);

  useEffect(() => { fetchMatches(); }, [fetchMatches]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/admin/potential-matches/generate`, {}, { headers });
      toast.success(
        lang === 'en'
          ? `Round ${res.data.round}: ${res.data.matches_count} new matches!`
          : `Ronde ${res.data.round}: ${res.data.matches_count} match baru!`
      );
      // Show the newly-generated round
      fetchMatches(res.data.round);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate');
    }
    setGenerating(false);
  };

  const handleReset = async () => {
    if (!window.confirm(lang === 'en'
      ? 'Reset ALL rounds? This will delete every potential match so you can start fresh.'
      : 'Reset SEMUA ronde? Ini akan menghapus semua potensial match agar bisa mulai ulang.')) return;
    setResetting(true);
    try {
      await axios.delete(`${API}/admin/potential-matches/reset`, { headers });
      toast.success(lang === 'en' ? 'All rounds cleared' : 'Semua ronde dibersihkan');
      setSelectedRound(null);
      fetchMatches();
    } catch (err) {
      console.error('[PotentialMatchesTab] handleReset failed:', err);
      toast.error('Failed to reset');
    }
    setResetting(false);
  };

  const [downloadingId, setDownloadingId] = useState(null);

  const downloadCard = async (matchId) => {
    const el = cardRefs.current[matchId];
    if (!el || downloadingId) return;
    setDownloadingId(matchId);
    // Yield to the browser so the disabled/loading UI paints before the heavy work starts.
    await new Promise(r => requestAnimationFrame(() => setTimeout(r, 0)));
    try {
      const canvas = await html2canvas(el, {
        scale: 1,
        backgroundColor: '#ffffff',
        useCORS: true,
        allowTaint: false,
        logging: false,
        imageTimeout: 3000,
        removeContainer: true,
      });
      // Use image/jpeg (smaller + faster encoding than png for photo-heavy cards).
      const link = document.createElement('a');
      link.download = `potential-match-${matchId.slice(0, 8)}.jpg`;
      link.href = canvas.toDataURL('image/jpeg', 0.9);
      link.click();
      toast.success(lang === 'en' ? 'Card downloaded!' : 'Kartu terunduh!');
    } catch (err) {
      console.error('[PotentialMatchesTab] downloadCard failed:', err);
      toast.error(lang === 'en' ? 'Download failed' : 'Gagal unduh');
    } finally {
      setDownloadingId(null);
    }
  };

  const getPhoto = (m) => m?.photos?.find(p => p) || '';

  // Mask "Andrew Setiawan" → "A****w S****n" — keep first + last letter of
  // each token, minimum 3 stars.
  const maskName = (name) => {
    if (!name) return 'Anonim';
    return name.split(/\s+/).filter(Boolean).map(tok => {
      if (tok.length <= 2) return `${tok[0]}*`;
      const stars = '*'.repeat(Math.max(3, tok.length - 2));
      return `${tok[0]}${stars}${tok[tok.length - 1]}`;
    }).join(' ');
  };

  // Mask "+6281234567890" → "+62•••••7890" — keep prefix + last 4.
  const maskWa = (wa) => {
    if (!wa) return '';
    const digits = String(wa).replace(/[^0-9+]/g, '');
    if (digits.length <= 6) return '•••••';
    const head = digits.startsWith('+') ? digits.slice(0, 3) : digits.slice(0, 2);
    const tail = digits.slice(-4);
    return `${head}•••••${tail}`;
  };

  const MemberInfo = ({ m, label }) => {
    if (!m) return null;
    const photo = getPhoto(m);
    return (
      <div className="flex-1 min-w-0">
        <p className="text-[9px] uppercase tracking-wider text-white/40 mb-1">{label}</p>
        <div className="flex items-start gap-2.5">
          {/* Portrait 3:4 — blurred photo (privacy) */}
          <div className="w-[90px] aspect-[3/4] rounded-lg overflow-hidden bg-white/10 flex-shrink-0 relative">
            {photo ? (
              <img
                src={photo}
                alt=""
                className="w-full h-full object-cover"
                style={{ filter: 'blur(14px)', transform: 'scale(1.15)' }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><User className="w-7 h-7 text-white/30" /></div>
            )}
            <div className="absolute inset-0 flex items-center justify-center bg-black/25">
              <span className="text-[9px] font-bold text-white/70 tracking-widest">RAHASIA</span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold text-base leading-tight truncate">{maskName(m.name)}</p>
            <p className="text-white/70 text-xs mt-0.5">{m.age} {lang === 'en' ? 'y.o' : 'thn'} · {m.gender === 'male' ? 'M' : 'F'}</p>
            <span className="inline-block text-[10px] mt-1 px-1.5 py-0.5 rounded-full bg-white/15 text-white/80">{m.category}</span>
            <div className="mt-1.5 space-y-0.5">
              {m.domicile && <p className="text-white/65 text-[11px] flex items-center gap-1"><MapPin className="w-3 h-3 flex-shrink-0" /><span className="truncate">{m.domicile?.split(' - ').slice(-1)[0]}</span></p>}
              {m.occupation && <p className="text-white/65 text-[11px] flex items-center gap-1"><Briefcase className="w-3 h-3 flex-shrink-0" /><span className="truncate">{m.occupation}</span></p>}
              {m.education && <p className="text-white/65 text-[11px] flex items-center gap-1"><GraduationCap className="w-3 h-3 flex-shrink-0" /><span className="truncate">{m.education}</span></p>}
              {m.whatsapp && <p className="text-white/65 text-[11px] flex items-center gap-1"><Phone className="w-3 h-3 flex-shrink-0" /><span className="truncate tabular-nums">{maskWa(m.whatsapp)}</span></p>}
              {m.mbti && <p className="text-white/65 text-[11px]">MBTI: <span className="text-white/85 font-medium">{m.mbti}</span></p>}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h3 className="t-text font-semibold flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#F59E0B]" />
            {lang === 'en' ? 'AI Potential Matches' : 'Potensial Match AI'}
          </h3>
          <p className="t-text-muted text-xs mt-1">
            {selectedRound
              ? (lang === 'en'
                  ? `Round ${selectedRound} - ${matches.length} match${matches.length === 1 ? '' : 'es'}`
                  : `Ronde ${selectedRound} - ${matches.length} match`)
              : (lang === 'en' ? 'No rounds generated yet' : 'Belum ada ronde dihasilkan')}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {availableRounds.length > 0 && (
            <Button onClick={handleReset} disabled={resetting || generating} variant="outline" size="sm" className="t-border t-text-secondary rounded-full text-xs" data-testid="reset-rounds-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1" />{lang === 'en' ? 'Reset All' : 'Reset Semua'}
            </Button>
          )}
          <Button onClick={handleGenerate} disabled={generating || resetting} className="bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-black rounded-full text-sm" data-testid="generate-matches-btn">
            {generating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {generating
              ? (lang === 'en' ? 'Generating...' : 'Menghasilkan...')
              : (availableRounds.length === 0
                  ? (lang === 'en' ? 'Generate Round 1' : 'Hasilkan Ronde 1')
                  : (lang === 'en' ? `Generate Round ${(availableRounds[availableRounds.length - 1] || 0) + 1}` : `Hasilkan Ronde ${(availableRounds[availableRounds.length - 1] || 0) + 1}`))}
          </Button>
        </div>
      </div>

      {/* Privacy hint (Batch 43) */}
      <div className="mb-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-[11px] t-text-secondary">
        <Sparkles className="w-3.5 h-3.5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
        <span>
          {lang === 'en'
            ? 'Names, photos, and phone numbers are masked for privacy. To find these potential matches, check the Member Catalog and filter by domicile.'
            : 'Nama, foto, dan nomor HP disamarkan untuk privasi. Cek di katalog member, filter berdasarkan domisili dan cari potential match mu ini.'}
        </span>
      </div>

      {/* Round switcher */}
      {availableRounds.length > 1 && (
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <span className="t-text-muted text-xs">{lang === 'en' ? 'View round:' : 'Lihat ronde:'}</span>
          {availableRounds.map(r => (
            <button
              key={r}
              onClick={() => fetchMatches(r)}
              data-testid={`round-tab-${r}`}
              className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                r === selectedRound
                  ? 'bg-[#F59E0B] text-black'
                  : 'glass t-text-secondary hover:bg-[var(--bg-input)]'
              }`}
            >
              {lang === 'en' ? `R${r}` : `R${r}`}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#F59E0B] border-t-transparent rounded-full animate-spin" /></div>
      ) : matches.length === 0 ? (
        <div className="text-center py-12 glass-card rounded-xl">
          <Sparkles className="w-12 h-12 mx-auto mb-4 t-text-muted" />
          <p className="t-text-secondary">{lang === 'en' ? 'No potential matches in this round' : 'Belum ada potensial match di ronde ini'}</p>
          <p className="t-text-muted text-xs mt-1">
            {availableRounds.length === 0
              ? (lang === 'en' ? 'Click "Generate Round 1" to discover compatible same-category pairs' : 'Klik "Hasilkan Ronde 1" untuk menemukan pasangan kategori yang cocok')
              : (lang === 'en' ? 'Click "Generate Round X" to find more pairs not used in previous rounds' : 'Klik "Hasilkan Ronde X" untuk mencari pasangan baru yang belum dipakai di ronde sebelumnya')}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {matches.map((match, i) => (
            <motion.div key={match.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
              {/* Downloadable card - compact, no forced aspect ratio */}
              <div
                ref={el => { if (el) cardRefs.current[match.id] = el; }}
                data-testid={`match-card-${match.id?.slice(0, 8)}`}
                className="relative rounded-2xl overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' }}
              >
                {/* Background pattern */}
                <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 25% 25%, white 1px, transparent 1px)', backgroundSize: '20px 20px' }} />

                {/* Content */}
                <div className="relative z-10 flex flex-col p-4 sm:p-5">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <img src="/images/mgi-logo.jpeg" alt="MGI" className="w-5 h-5 rounded" />
                      <span className="text-white/40 text-[10px] font-medium tracking-wider">MATCH GROUP INDONESIA</span>
                    </div>
                    <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F59E0B]/20">
                      <Sparkles className="w-2.5 h-2.5 text-[#F59E0B]" />
                      <span className="text-[#F59E0B] text-[9px] font-bold">AI MATCH</span>
                    </div>
                  </div>

                  {/* Two members side by side */}
                  <div className="flex gap-3 sm:gap-4">
                    <MemberInfo m={match.member} label={lang === 'en' ? 'MEMBER' : 'ANGGOTA'} />

                    {/* Heart connector */}
                    <div className="flex-shrink-0 flex flex-col items-center justify-center pt-6">
                      <div className="w-7 h-7 rounded-full bg-[#D98A94]/20 flex items-center justify-center">
                        <Heart className="w-3.5 h-3.5 text-[#D98A94]" fill="#D98A94" />
                      </div>
                    </div>

                    <MemberInfo m={match.match} label={lang === 'en' ? 'POTENTIAL MATCH' : 'POTENSIAL MATCH'} />
                  </div>

                  {/* Reason */}
                  <div className="mt-3 pt-2.5" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    <p className="text-white/75 text-[11px] leading-snug italic">
                      "{lang === 'en' ? match.reason_en : match.reason_id}"
                    </p>
                  </div>
                </div>
              </div>

              {/* Download button - outside the card */}
              <div className="flex justify-end mt-2">
                <button
                  onClick={() => downloadCard(match.id)}
                  disabled={downloadingId === match.id}
                  data-testid={`download-card-${match.id?.slice(0, 8)}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full glass text-xs t-text-secondary hover:text-[#F59E0B] transition ${downloadingId === match.id ? 'opacity-60 cursor-wait' : ''}`}
                >
                  <Download className={`w-3.5 h-3.5 ${downloadingId === match.id ? 'animate-pulse' : ''}`} />
                  {downloadingId === match.id
                    ? (lang === 'en' ? 'Saving...' : 'Menyimpan...')
                    : (lang === 'en' ? 'Download Card' : 'Unduh Kartu')}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
